import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  Users, Briefcase, Calendar, Euro, TrendingUp, 
  Award, MessageSquare, Settings, Megaphone, Loader2,
  BarChart3, ArrowUpRight, ArrowDownRight, Shield, Ticket, CreditCard
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Progress } from '../../components/ui/progress';
import AdminLanguageSelector, { useAdminLanguage } from '../../components/AdminLanguageSelector';
import { adminTranslations } from '../../i18n/translations';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RANK_COLORS = {
  pro: "#4A90D9",
  elite: "#9B59B6",
  master: "#F1C40F",
  grandmaster: "#E74C3C",
  legendary: "#E07A5F"
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { language } = useAdminLanguage();
  const t = adminTranslations[language] || adminTranslations.fr;
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/');
      return;
    }

    const fetchStats = async () => {
      try {
        const response = await fetch(`${API_URL}/api/admin/stats`, {
          headers: {
            'Authorization': `Bearer ${token || localStorage.getItem('token')}`,
          },
        });
        if (response.ok) {
          const data = await response.json();
          setStats(data);
        }
      } catch (error) {
        console.error('Error fetching stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [user, token, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background pt-24 pb-16 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-accent" />
      </div>
    );
  }

  const totalRankProviders = stats ? Object.values(stats.rank_distribution).reduce((a, b) => a + b, 0) : 0;

  return (
    <div className="min-h-screen bg-background pt-24 pb-16" data-testid="admin-dashboard">
      <div className="container-app">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-10">
          <div>
            <h1 className="font-heading text-3xl sm:text-4xl font-semibold mb-2">
              Administration AFY
            </h1>
            <p className="text-muted-foreground">
              Vue d'ensemble de la plateforme
            </p>
          </div>
          <div className="flex gap-3 flex-wrap">
            <Button variant="outline" onClick={() => navigate('/admin/users')}>
              <Users className="w-4 h-4 mr-2" />
              Utilisateurs
            </Button>
            <Button className="bg-amber-500 hover:bg-amber-600 text-white" onClick={() => navigate('/admin/verifications')} data-testid="admin-verifications-btn">
              <Shield className="w-4 h-4 mr-2" />
              Vérifications
            </Button>
            <Button variant="outline" onClick={() => navigate('/admin/ads')}>
              <Megaphone className="w-4 h-4 mr-2" />
              Publicités
            </Button>
            <Button variant="outline" onClick={() => navigate('/admin/settings')}>
              <Settings className="w-4 h-4 mr-2" />
              Paramètres
            </Button>
            <Button variant="outline" onClick={() => navigate('/admin/commission')} data-testid="admin-commission-btn">
              <Euro className="w-4 h-4 mr-2" />
              Commission
            </Button>
            <Button variant="outline" onClick={() => navigate('/admin/payouts')} data-testid="admin-payouts-btn">
              <Euro className="w-4 h-4 mr-2" />
              Reversements
            </Button>
            <Button variant="outline" onClick={() => navigate('/admin/vouchers')} data-testid="admin-vouchers-btn">
              <Ticket className="w-4 h-4 mr-2" />
              Vouchers
            </Button>
            <Button 
              className="bg-gradient-to-r from-violet-500 to-purple-600 text-white hover:from-violet-600 hover:to-purple-700" 
              onClick={() => navigate('/admin/stripe')} 
              data-testid="admin-stripe-btn"
            >
              <CreditCard className="w-4 h-4 mr-2" />
              Stripe Dashboard
            </Button>
          </div>
        </div>

        {/* Main Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          <Card className="dashboard-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-muted-foreground text-sm">Utilisateurs totaux</p>
                  <p className="text-3xl font-bold">{stats?.users?.total || 0}</p>
                  <p className="text-xs text-success flex items-center mt-1">
                    <ArrowUpRight className="w-3 h-3 mr-1" />
                    +{stats?.recent_users_7_days || 0} cette semaine
                  </p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center">
                  <Users className="w-6 h-6 text-blue-500" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="dashboard-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-muted-foreground text-sm">Prestataires</p>
                  <p className="text-3xl font-bold">{stats?.users?.providers || 0}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {stats?.users?.clients || 0} clients
                  </p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center">
                  <Briefcase className="w-6 h-6 text-accent" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="dashboard-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-muted-foreground text-sm">Réservations</p>
                  <p className="text-3xl font-bold">{stats?.bookings?.total || 0}</p>
                  <p className="text-xs text-amber-500 flex items-center mt-1">
                    {stats?.bookings?.pending || 0} en attente
                  </p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-purple-500/10 flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-purple-500" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="dashboard-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-muted-foreground text-sm">Revenus totaux</p>
                  <p className="text-3xl font-bold">{stats?.revenue?.total?.toFixed(0) || 0}€</p>
                  <p className="text-xs text-success flex items-center mt-1">
                    <Euro className="w-3 h-3 mr-1" />
                    {stats?.revenue?.commission?.toFixed(0) || 0}€ commission
                  </p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-success/10 flex items-center justify-center">
                  <Euro className="w-6 h-6 text-success" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Rank Distribution */}
          <Card className="dashboard-card">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <Award className="w-5 h-5" />
                Distribution des rangs
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {Object.entries(stats?.rank_distribution || {}).map(([rank, count]) => {
                  const percentage = totalRankProviders > 0 ? (count / totalRankProviders) * 100 : 0;
                  return (
                    <div key={rank}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium capitalize">{rank}</span>
                        <span className="text-muted-foreground">{count} ({percentage.toFixed(0)}%)</span>
                      </div>
                      <Progress 
                        value={percentage} 
                        className="h-2"
                        style={{ '--progress-color': RANK_COLORS[rank] }}
                      />
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Category Stats */}
          <Card className="dashboard-card">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <BarChart3 className="w-5 h-5" />
                Prestataires par catégorie
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {Object.entries(stats?.category_stats || {}).map(([cat, count]) => (
                  <div key={cat} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                    <span className="capitalize">{cat.replace('_', ' ')}</span>
                    <span className="font-semibold">{count}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card className="dashboard-card">
            <CardHeader>
              <CardTitle className="font-heading">Actions rapides</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-3">
              <Button 
                variant="outline" 
                className="h-auto py-4 flex-col"
                onClick={() => navigate('/admin/users')}
              >
                <Users className="w-6 h-6 mb-2" />
                <span>Gérer utilisateurs</span>
              </Button>
              <Button 
                variant="outline" 
                className="h-auto py-4 flex-col"
                onClick={() => navigate('/admin/providers')}
              >
                <Briefcase className="w-6 h-6 mb-2" />
                <span>Gérer prestataires</span>
              </Button>
              <Button 
                variant="outline" 
                className="h-auto py-4 flex-col"
                onClick={() => navigate('/admin/bookings')}
              >
                <Calendar className="w-6 h-6 mb-2" />
                <span>Voir réservations</span>
              </Button>
              <Button 
                variant="outline" 
                className="h-auto py-4 flex-col"
                onClick={() => navigate('/admin/ads')}
              >
                <Megaphone className="w-6 h-6 mb-2" />
                <span>Gérer publicités</span>
              </Button>
            </CardContent>
          </Card>

          {/* Recent Activity */}
          <Card className="dashboard-card">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <TrendingUp className="w-5 h-5" />
                Activité récente (7 jours)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 rounded-lg bg-muted/50">
                  <div className="flex items-center gap-3">
                    <Users className="w-5 h-5 text-blue-500" />
                    <span>Nouveaux utilisateurs</span>
                  </div>
                  <span className="font-bold text-xl">{stats?.recent_users_7_days || 0}</span>
                </div>
                <div className="flex items-center justify-between p-4 rounded-lg bg-muted/50">
                  <div className="flex items-center gap-3">
                    <Calendar className="w-5 h-5 text-purple-500" />
                    <span>Nouvelles réservations</span>
                  </div>
                  <span className="font-bold text-xl">{stats?.bookings?.recent_7_days || 0}</span>
                </div>
                <div className="flex items-center justify-between p-4 rounded-lg bg-muted/50">
                  <div className="flex items-center gap-3">
                    <Briefcase className="w-5 h-5 text-accent" />
                    <span>Missions complétées</span>
                  </div>
                  <span className="font-bold text-xl">{stats?.bookings?.completed || 0}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
